import { Anchor, Button, Center, Paper, PasswordInput, Stack, Text, TextInput, Title } from "@mantine/core";
import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link } from "react-router";

import { api, call } from "../api/client";
import { setToken } from "../lib/session";

function AuthCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Center mih="100dvh" p="md">
      <Paper withBorder p="xl" w="100%" maw={380}>
        <Stack>
          <Title order={2} ta="center">
            🥗 {title}
          </Title>
          {children}
        </Stack>
      </Paper>
    </Center>
  );
}

export function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const login = useMutation({
    mutationFn: () => call(api.POST("/api/v1/auth/login", { body: { username, password } })),
    onSuccess: (s) => setToken(s.token),
    meta: { errorTitle: "Couldn't sign in" },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate();
  };
  return (
    <AuthCard title="Sign in">
      <form onSubmit={submit}>
        <Stack>
          <TextInput label="Username" value={username} onChange={(e) => setUsername(e.currentTarget.value)} autoComplete="username" required autoFocus />
          <PasswordInput label="Password" value={password} onChange={(e) => setPassword(e.currentTarget.value)} autoComplete="current-password" required />
          <Button type="submit" loading={login.isPending}>
            Sign in
          </Button>
        </Stack>
      </form>
      <Text size="sm" ta="center">
        Have an invite code?{" "}
        <Anchor component={Link} to="/register">
          Create an account
        </Anchor>
      </Text>
    </AuthCard>
  );
}

export function RegisterPage() {
  const [form, setForm] = useState({ invite_code: "", username: "", password: "" });
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const register = useMutation({
    mutationFn: () => call(api.POST("/api/v1/auth/register", { body: { ...form, timezone } })),
    onSuccess: (s) => setToken(s.token),
    meta: { errorTitle: "Couldn't create the account" },
  });
  const field = (key: keyof typeof form) => ({
    value: form[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [key]: e.currentTarget.value }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    register.mutate();
  };
  return (
    <AuthCard title="Create account">
      <form onSubmit={submit}>
        <Stack>
          <TextInput label="Invite code" {...field("invite_code")} required autoFocus />
          <TextInput label="Username" description="Lowercase letters, digits, . _ -" {...field("username")} autoComplete="username" required />
          <PasswordInput label="Password" description="At least 10 characters" {...field("password")} autoComplete="new-password" minLength={10} required />
          <Text size="xs" c="dimmed">
            Timezone: {timezone} (you can change it in Settings)
          </Text>
          <Button type="submit" loading={register.isPending}>
            Create account
          </Button>
        </Stack>
      </form>
      <Text size="sm" ta="center">
        <Anchor component={Link} to="/login">
          Back to sign in
        </Anchor>
      </Text>
    </AuthCard>
  );
}
